<template>
  <div class="members-page">
    <!-- 상단: 타이틀 + 필터 + 페이지네이션 -->
    <div class="head">
      <div class="title-row">
        <h2>회원 관리</h2>
        <span v-if="loaded" class="total">전체 {{ totalText }}명</span>
      </div>

      <!-- 필터 버튼 + 정렬 + 페이지네이션 -->
      <div class="filters-row">
        <div class="filters">
          <button
            v-for="f in filterOptions"
            :key="f.key"
            type="button"
            :class="['filter-btn', { active: expiryFilter === f.key }]"
            :aria-pressed="expiryFilter === f.key"
            @click="setFilter(f.key)"
          >
            {{ f.label }}
          </button>
        </div>
        <div class="sorts">
          <button
            v-for="s in sortOptions"
            :key="s.key"
            type="button"
            :class="['sort-btn', { active: sortBy === s.key }]"
            @click="toggleSort(s.key)"
          >
            {{ s.label }}
            <span v-if="sortBy === s.key">{{
              sortOrder === "asc" ? "↑" : "↓"
            }}</span>
          </button>
        </div>

        <!-- 페이지네이션 -->
        <div class="pagination">
          <button
            type="button"
            class="page-btn"
            :disabled="currentPage === 1 || loading"
            aria-label="이전 페이지"
            @click="goPrev"
          >
            ‹
          </button>
          <div class="page-indicator">
            {{ currentPage }} / {{ totalPages }} 페이지
          </div>
          <button
            type="button"
            class="page-btn"
            :disabled="loading || (!hasMore && currentPage >= totalPages)"
            aria-label="다음 페이지"
            @click="goNext"
          >
            ›
          </button>
        </div>
      </div>

      <!-- 검색 (Enter로 적용) -->
      <div class="search">
        <span class="icon">🔍</span>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="이름 또는 전화번호로 검색"
          @keyup.enter="submitSearch"
        />
        <button
          v-if="searchQuery || state.q"
          type="button"
          class="clear"
          @click="clearSearch"
        >
          지우기
        </button>
      </div>
    </div>

    <!-- 회원 표: 머리글 한 줄 + 회원마다 한 줄(누르면 회원 상세). 좁은 화면에서는 카드 -->
    <div class="table" :class="{ busy: loading && members.length > 0 }">
      <div class="table-head" aria-hidden="true">
        <span>이름·전화</span>
        <span>수강권</span>
        <span>남은 횟수</span>
        <span>만료일</span>
        <span>최근 출석</span>
        <span>특이사항</span>
      </div>
      <MemberRow v-for="m in members" :key="m.user_id" :member="m" />
      <p v-if="loading && members.length === 0" class="empty">불러오는 중…</p>
      <p v-else-if="loadError" class="empty">
        회원 목록을 불러오지 못했습니다.
        <button type="button" class="retry" @click="fetchMembers">
          다시 시도
        </button>
      </p>
      <p v-else-if="!loading && members.length === 0" class="empty">
        표시할 회원이 없습니다.
      </p>
    </div>
  </div>
</template>

<script>
import { isNavigationFailure, NavigationFailureType } from "vue-router";
import MemberRow from "@/components/MemberRow.vue";
import { apiJson } from "@/lib/api";

// 필터(expiry_filter)·정렬(sort_by) 값은 서버 GET /v1/admin/members가 아는 값만 쓴다
const FILTER_OPTIONS = [
  { key: "all", label: "전체" },
  { key: "has_membership", label: "수강권 있음" },
  { key: "week", label: "1주 미만" },
  { key: "two_weeks", label: "2주 미만" },
  { key: "low_count", label: "3회 이하" },
  { key: "no_attendance_week", label: "미출석 1주" },
];

// first: 처음 누를 때 방향. "최근 수정"(특이사항·메모가 마지막으로 바뀐 순, 사장님 2026-10-25)은 최근 것부터, 나머지는 오름차순
const SORT_OPTIONS = [
  { key: "expired_at", label: "남은일자", first: "asc" },
  { key: "remaining_count", label: "남은횟수", first: "asc" },
  { key: "last_attendance", label: "최근출석", first: "asc" },
  { key: "recent_note", label: "최근 수정", first: "desc" },
];

const PAGE_SIZE = 20;

// 목록 상태 기본값. URL 쿼리에 없거나 잘못된 값이면 이 값을 쓴다
// (sort가 ""이면 서버 기본 정렬 = 만료일 빠른 순)
const DEFAULT_STATE = Object.freeze({
  filter: "all",
  sort: "",
  order: "asc",
  q: "",
  page: 1,
});

// 같은 이름이 여러 번 오면(?page=1&page=2) 첫 값만 본다. 값 없는 키(?q)는 ""
function queryValue(v) {
  const first = Array.isArray(v) ? v[0] : v;
  return first == null ? "" : String(first);
}

// $route.query(?filter=&sort=&order=&q=&page=) → 목록 상태
function parseListQuery(query) {
  const filter = queryValue(query.filter);
  const sort = queryValue(query.sort);
  const order = queryValue(query.order);
  const page = queryValue(query.page);
  const sortOption = SORT_OPTIONS.find((s) => s.key === sort);
  // 방향이 없거나 잘못되면 그 정렬을 처음 누를 때 방향("최근 수정"은 최근 먼저)
  let sortOrder = DEFAULT_STATE.order;
  if (sortOption) {
    sortOrder = order === "asc" || order === "desc" ? order : sortOption.first;
  }
  return {
    filter: FILTER_OPTIONS.some((f) => f.key === filter)
      ? filter
      : DEFAULT_STATE.filter,
    sort: sortOption ? sort : DEFAULT_STATE.sort,
    order: sortOrder,
    q: queryValue(query.q).trim(),
    page: /^[1-9]\d{0,5}$/.test(page) ? Number(page) : DEFAULT_STATE.page,
  };
}

// 목록 상태 → URL 쿼리. 기본값인 칸은 빼서 주소를 짧게 둔다
function buildListQuery(state) {
  const query = {};
  if (state.filter !== DEFAULT_STATE.filter) query.filter = state.filter;
  if (state.sort) {
    query.sort = state.sort;
    query.order = state.order;
  }
  if (state.q) query.q = state.q;
  if (state.page > 1) query.page = String(state.page);
  return query;
}

export default {
  name: "MemberListView",
  components: { MemberRow },
  data() {
    return {
      filterOptions: FILTER_OPTIONS,
      sortOptions: SORT_OPTIONS,
      // URL 쿼리에서 읽은 목록 상태. 직접 바꾸지 않고 updateQuery로 주소를 바꾸면
      // $route.query 감시가 다시 읽는다 (회원 상세에서 뒤로 오면 그대로 복원)
      state: { ...DEFAULT_STATE },
      listPath: "",
      searchQuery: "", // 검색 입력칸. Enter를 눌러야 state.q(주소)에 들어간다
      members: [],
      limit: PAGE_SIZE,
      total: 0,
      hasMore: false,
      loading: false,
      loaded: false,
      loadError: false,
      requestSeq: 0,
    };
  },
  computed: {
    expiryFilter() {
      return this.state.filter;
    },
    sortBy() {
      return this.state.sort;
    },
    sortOrder() {
      return this.state.order;
    },
    currentPage() {
      return this.state.page;
    },
    totalPages() {
      if (this.total === 0) return 1;
      return Math.ceil(this.total / this.limit);
    },
    totalText() {
      return Number(this.total || 0).toLocaleString("ko-KR");
    },
  },
  watch: {
    "$route.query"() {
      this.syncFromRoute();
    },
  },
  created() {
    this.listPath = this.$route.path;
    this.syncFromRoute();
  },
  beforeUnmount() {
    // 목록을 떠난 뒤 도착한 응답은 버린다(빈 페이지 옮기기가 떠난 화면에서 목록으로 다시 끌고 오지 않게)
    this.requestSeq++;
  },
  methods: {
    syncFromRoute() {
      // 회원 상세로 떠날 때도 $route가 바뀌므로 목록 주소일 때만 읽는다
      if (this.$route.path !== this.listPath) return;
      const first = this.requestSeq === 0; // 처음 읽기(created)
      const prevQ = this.state.q;
      this.state = parseListQuery(this.$route.query);
      // 검색어가 그대로면 검색칸에 치던 글자(Enter 전)는 남긴다(신규회원 관리와 같음). 예전에는 쪽 버튼을 누르면 지워졌다
      if (first || this.state.q !== prevQ) this.searchQuery = this.state.q;
      this.fetchMembers();
    },
    // 목록 상태를 주소에 담는다. replace라서 필터·페이지를 바꿔도 방문 기록이 쌓이지 않고,
    // 뒤로 가기는 목록에 오기 전 화면으로 간다
    async updateQuery(patch) {
      // 목록을 떠난 뒤(늦게 온 응답의 빈 페이지 옮기기 등)에는 주소를 바꾸지 않는다. 예전에는 다른 메뉴로 간 사람을 목록으로 되돌렸다
      if (this.$route.path !== this.listPath) return;
      const next = { ...this.state, ...patch };
      try {
        const failure = await this.$router.replace({
          path: this.listPath,
          query: buildListQuery(next),
        });
        // 주소가 그대로면(같은 검색어로 다시 Enter 등) 라우트가 안 바뀌므로 직접 다시 읽는다
        if (isNavigationFailure(failure, NavigationFailureType.duplicated)) {
          this.fetchMembers();
        }
      } catch {
        // 이동 오류는 라우터 가드 쪽 처리에 맡긴다
      }
    },
    async fetchMembers() {
      const seq = ++this.requestSeq;
      const { filter, sort, order, q, page } = this.state;
      this.loading = true;
      this.loadError = false;
      try {
        const params = new URLSearchParams({
          expiry_filter: filter,
          limit: String(this.limit),
          offset: String((page - 1) * this.limit),
        });
        if (q) params.append("search", q);
        if (sort) {
          params.append("sort_by", sort);
          params.append("sort_order", order);
        }

        const res = await apiJson(`/v1/admin/members?${params.toString()}`);
        // 그사이 조건이 또 바뀌었거나 목록을 떠났으면 늦게 온 응답은 버린다
        if (seq !== this.requestSeq || this.$route.path !== this.listPath) {
          return;
        }

        this.members = Array.isArray(res?.members) ? res.members : [];
        this.total = Number.isFinite(res?.total) ? res.total : 0;
        this.hasMore = !!res?.has_more;
        this.loaded = true;

        // 뒤로 와서 보니 회원이 줄어 이 페이지가 비었으면 마지막 페이지로 옮긴다
        if (
          this.members.length === 0 &&
          this.total > 0 &&
          page > this.totalPages
        ) {
          this.updateQuery({ page: this.totalPages });
        }
      } catch {
        if (seq !== this.requestSeq) return;
        this.members = [];
        this.total = 0;
        this.hasMore = false;
        this.loaded = false;
        this.loadError = true;
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },
    submitSearch() {
      this.updateQuery({ q: this.searchQuery.trim(), page: 1 });
    },
    clearSearch() {
      this.searchQuery = "";
      this.updateQuery({ q: "", page: 1 });
    },
    // 정렬·필터는 검색칸에 친 글자(Enter 전)도 함께 적용한다(main과 같음). 예전에는 글자가 지워지고 적용되지 않았다(2차 리뷰)
    toggleSort(key) {
      const q = this.searchQuery.trim();
      if (this.sortBy === key) {
        this.updateQuery({
          order: this.sortOrder === "asc" ? "desc" : "asc",
          q,
          page: 1,
        });
      } else {
        const option = SORT_OPTIONS.find((s) => s.key === key);
        this.updateQuery({
          sort: key,
          order: option ? option.first : "asc",
          q,
          page: 1,
        });
      }
    },
    setFilter(filter) {
      this.updateQuery({ filter, q: this.searchQuery.trim(), page: 1 });
    },
    goPrev() {
      if (this.loading) return;
      if (this.currentPage === 1) return;
      this.updateQuery({ page: this.currentPage - 1 });
    },
    goNext() {
      if (this.loading) return;
      const canNext =
        this.hasMore || (this.total && this.currentPage < this.totalPages);
      if (!canNext) return;
      this.updateQuery({ page: this.currentPage + 1 });
    },
  },
};
</script>

<style scoped>
.members-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #151718;
  border-radius: 10px;
  padding: 16px;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
}

.head {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.title-row {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 12px;
}

.head h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 400;
  color: #fff;
}

.total {
  color: #8e8e8e;
  font-size: 15px;
}

/* 필터 + 정렬 + 페이지네이션 (좁으면 다음 줄로 넘김) */
.filters-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 10px 16px;
}

/* 필터 버튼 */
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-btn {
  height: 40px;
  padding: 0 16px;
  border-radius: 6px;
  background: #1b1d1e;
  color: #9d9d9d;
  border: 1px solid #333;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-btn:hover:not(.active) {
  background: #252728;
}

.filter-btn.active {
  background: #78ff47;
  color: #000;
  border-color: #78ff47;
}

.sorts {
  display: flex;
  gap: 8px;
}
.sort-btn {
  background: transparent;
  border: 1px solid #555;
  color: #aaa;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
}
.sort-btn:hover {
  border-color: #888;
}
.sort-btn.active {
  background: #333;
  color: #fff;
  border-color: #888;
}

/* 페이지네이션 */
.pagination {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}

.page-indicator {
  min-width: 80px;
  text-align: center;
  padding: 8px 16px;
  border: 1px solid #333;
  border-radius: 6px;
  color: #fff;
  background: #1b1d1e;
  font-size: 14px;
  white-space: nowrap;
}

.page-btn {
  min-width: 40px;
  height: 40px;
  padding: 0 8px;
  border-radius: 6px;
  background: #1b1d1e;
  color: #fff;
  border: 1px solid #333;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 18px;
}

.page-btn:hover:not(:disabled) {
  background: #252728;
}

.page-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* 검색 */
.search {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
}

.search .icon {
  position: absolute;
  left: 12px;
}

.search input {
  width: 100%;
  height: 54px;
  background: #151718;
  border-radius: 5px;
  border: 1px solid #333;
  outline: none;
  color: #fff;
  padding: 0 90px 0 36px;
  min-width: 0;
  box-sizing: border-box;
}

.search input:focus {
  border-color: #555;
}

.search .clear {
  position: absolute;
  right: 8px;
  height: 34px;
  padding: 0 10px;
  border-radius: 5px;
  border: 1px solid #333;
  background: #1b1d1e;
  color: #fff;
  cursor: pointer;
}

.search .clear:hover {
  background: #252728;
}

/* 회원 표: 열 너비·간격 변수는 MemberRow 줄과 머리글이 같이 쓴다.
   이름·전화 칸은 전화번호 "010-1234-5678", 수강권 칸은 "+ 10회권 10.26 시작"이 잘리지 않는 최소 너비를 둔다
   (사이드메뉴가 보이는 1101~1220px 화면에서 끝자리·날짜가 말줄임으로 잘렸다). 특이사항 칸은 말줄임이라 가장 먼저 좁아진다 */
.table {
  --member-cols: minmax(112px, 1.3fr) minmax(132px, 1.4fr) 76px 104px 96px
    minmax(0, 1.5fr);
  --member-gap: 16px;
  --member-pad-x: 20px;
  display: grid;
  gap: 8px;
  width: 100%;
  min-width: 0;
  transition: opacity 0.2s ease;
}

.table.busy {
  opacity: 0.6;
}

/* 줄에는 1px 테두리가 있어 머리글 안쪽 여백을 1px 더 준다 */
.table-head {
  display: grid;
  grid-template-columns: var(--member-cols);
  gap: var(--member-gap);
  padding: 0 calc(var(--member-pad-x) + 1px);
  color: #8e8e8e;
  font-size: 13px;
  line-height: 1.4;
  white-space: nowrap;
}

.table-head span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.empty {
  margin: 0;
  color: #c8c8c8;
  padding: 12px;
}

.retry {
  margin-left: 8px;
  height: 32px;
  padding: 0 12px;
  border-radius: 5px;
  border: 1px solid #333;
  background: #1b1d1e;
  color: #fff;
  cursor: pointer;
}

.retry:hover {
  background: #252728;
}

/* 반응형 */
@media (max-width: 1200px) {
  .table {
    --member-cols: minmax(112px, 1.3fr) minmax(132px, 1.4fr) 64px 92px 88px
      minmax(0, 1.5fr);
    --member-gap: 10px;
    --member-pad-x: 14px;
  }
}

@media (max-width: 920px) {
  .filters-row {
    flex-direction: column;
    align-items: stretch;
  }

  .pagination {
    justify-content: flex-end;
  }
}

/* 좁은 화면: 머리글을 숨기고 줄을 카드로 쌓는다(MemberRow가 "라벨: 값"으로 바뀜) */
@media (max-width: 860px) {
  .table {
    gap: 12px;
  }

  .table-head {
    display: none;
  }
}

@media (max-width: 720px) {
  .head h2 {
    font-size: 20px;
  }

  .filter-btn {
    flex: 1;
    min-width: 80px;
  }
}
</style>
